// helpers
import { handleErrorAlert } from "../../../common/utils/helpers/handleErrorAlert";
import ApiHelper from "../../../common/utils/apiHelper";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import {
  buildAirlineMilesAndMoreBookingFields,
  readAirlineMilesAndMoreNumber
} from "../../../common/utils/transport/airlineBookingPayload";
import { type TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
import { resolveFrankfurtCardApiPaths } from "./helpers/resolveFrankfurtCardApiPaths";
import { getFirstTravelerName } from "./helpers/getFirstTravelerName";
// actions / selectors
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import {
  type FrankfurtCardBookingForm,
  type FrankfurtCardOffer,
  type FrankfurtCardOffersResponse,
  type FrankfurtCardPassenger
} from "./types";

// Toggle mock for Frankfurt Card offers + estimation
const USE_MOCK_FRANKFURT_CARD = false;
// Toggle to make the mocked estimation fail with a 400 validation error (needs USE_MOCK_FRANKFURT_CARD = true)
const USE_MOCK_FRANKFURT_CARD_ESTIMATION_ERROR = false;

export const initialFrankfurtCardPassenger: FrankfurtCardPassenger = {
  firstName: "",
  lastName: "",
  hasRemove: false
};

const MOCK_FRANKFURT_CARD_PRICES: Record<string, string> = {
  "FC-1DAY": "27.00",
  "FC-2DAY": "42.00"
};

const MOCK_FRANKFURT_CARD_OFFERS_RESPONSE: FrankfurtCardOffersResponse = {
  provider_title: "FRANKFURT CARD",
  provider_description:
    "Free travel on all public transport in Frankfurt city incl. the airport (zones 5000 and 5090, 2nd class).",
  provider_logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/390c0357-516c-45ae-91b6-152fb9996817/65249b87c14fafd.png",
    name: "frankfurt-card"
  },
  highlight:
    "<p>One card is required per person aged 6 and over.</p><p>Children up to 5 years travel free of charge.</p><p>Valid on the selected day(s) until 05:00 the following day.</p>",
  cancellation_policy: "Cannot be changed, cancelled or refunded.",
  items: [
    {
      name: "Frankfurt Card 1 Day",
      offer_code: "FC-1DAY",
      short_description: "Public transport and attraction discounts in Frankfurt.",
      cancellation_policy: "Cannot be changed, cancelled or refunded."
    },
    {
      name: "Frankfurt Card 2 Days",
      offer_code: "FC-2DAY",
      short_description: "Public transport and attraction discounts in Frankfurt.",
      cancellation_policy: "Cannot be changed, cancelled or refunded."
    }
  ]
};

const buildMockEstimationResponse = (offerCode: string | null, passengers: Array<Record<string, any>>) => {
  const offer =
    MOCK_FRANKFURT_CARD_OFFERS_RESPONSE?.items?.find(item => item.offer_code === offerCode) ??
    MOCK_FRANKFURT_CARD_OFFERS_RESPONSE?.items?.[0];
  const price = MOCK_FRANKFURT_CARD_PRICES[offer?.offer_code ?? ""] ?? "27.00";
  const total = (Number(price) * passengers.length).toFixed(2);

  return {
    type: "frankfurt-card",
    name: offer?.name,
    offer_code: offer?.offer_code,
    formatted_price: `${total} EUR`,
    miles_and_more: `${Math.round(Number(total) * 2)} miles`,
    details: {
      session_id: "mock-frankfurt-card-session",
      validity: "Valid on 23.10.2026 until 05:00 the following day.",
      passengers: passengers.map(passenger => ({
        first_name: passenger?.first_name,
        last_name: passenger?.last_name,
        price,
        currency: "EUR"
      }))
    }
  };
};

const MOCK_FRANKFURT_CARD_ESTIMATION_ERROR_RESPONSE = {
  code: 400,
  message: "Validation failed",
  debug: null,
  fields: [
    {
      field: "details.passengers[0].lastName",
      errorMessage: "This value should not be blank."
    }
  ]
};

export const frankfurtCardActionTypes = {
  RESET_FRANKFURT_CARD_STATE: "RESET_FRANKFURT_CARD_STATE",
  LOAD_FRANKFURT_CARD_OFFERS: "LOAD_FRANKFURT_CARD_OFFERS",
  SET_FRANKFURT_CARD_SELECTED_OFFER: "SET_FRANKFURT_CARD_SELECTED_OFFER",
  UPDATE_FRANKFURT_CARD_PASSENGERS_LIST: "UPDATE_FRANKFURT_CARD_PASSENGERS_LIST",
  SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO: "SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO",
  UPDATE_FRANKFURT_CARD_SHOW_VALIDATION_ERRORS: "UPDATE_FRANKFURT_CARD_SHOW_VALIDATION_ERRORS",
  UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR: "UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR",
  SET_FRANKFURT_CARD_BOOKING_FORM: "SET_FRANKFURT_CARD_BOOKING_FORM",
  TOGGLE_FRANKFURT_CARD_BOOKING_ERROR: "TOGGLE_FRANKFURT_CARD_BOOKING_ERROR",
  SET_FRANKFURT_CARD_BOOKING_RESPONSE_DATA: "SET_FRANKFURT_CARD_BOOKING_RESPONSE_DATA",
  TOGGLE_FRANKFURT_CARD_PAYMENT_MODAL: "TOGGLE_FRANKFURT_CARD_PAYMENT_MODAL"
};

const EMPTY_BOOKING_FORM: FrankfurtCardBookingForm = {
  nameForm: "",
  emailForm: "",
  commentForm: ""
};

export const initialState = {
  offers: null as FrankfurtCardOffersResponse,
  selectedOffer: null as FrankfurtCardOffer | null,
  passengerList: [{ ...initialFrankfurtCardPassenger }] as FrankfurtCardPassenger[],
  prebookingInfoResponse: null as any,
  prebookingError: null as any,
  showValidationErrors: false,
  dataForBooking: { ...EMPTY_BOOKING_FORM } as FrankfurtCardBookingForm,
  // Guest name last prefilled from the first traveller; tells a prefilled name apart from one the guest typed.
  prefilledNameForm: "",
  bookingError: false,
  bookingResponseData: null as any,
  showPaymentModal: false
};

const frankfurtCardModule = (state = initialState, action: any) => {
  switch (action.type) {
    case frankfurtCardActionTypes.RESET_FRANKFURT_CARD_STATE:
      return {
        ...initialState,
        passengerList: [{ ...initialFrankfurtCardPassenger }],
        dataForBooking: { ...EMPTY_BOOKING_FORM }
      };
    case frankfurtCardActionTypes.LOAD_FRANKFURT_CARD_OFFERS:
      return {
        ...state,
        offers: action.offers
      };
    case frankfurtCardActionTypes.SET_FRANKFURT_CARD_SELECTED_OFFER:
      return {
        ...state,
        selectedOffer: action.selectedOffer,
        prebookingInfoResponse: null,
        prebookingError: null
      };
    case frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PASSENGERS_LIST:
      return {
        ...state,
        passengerList: action.passengerList
      };
    case frankfurtCardActionTypes.SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO: {
      const nameForm = state.dataForBooking.nameForm;
      // Prefill the guest name when the summary opens, unless the guest typed their own.
      const canPrefillName =
        action.prebookingInfoResponse && (!nameForm.trim() || nameForm === state.prefilledNameForm);

      if (!canPrefillName) {
        return {
          ...state,
          prebookingInfoResponse: action.prebookingInfoResponse
        };
      }

      const firstTravelerName = getFirstTravelerName(state.passengerList);

      return {
        ...state,
        prebookingInfoResponse: action.prebookingInfoResponse,
        dataForBooking: { ...state.dataForBooking, nameForm: firstTravelerName },
        prefilledNameForm: firstTravelerName
      };
    }
    case frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_SHOW_VALIDATION_ERRORS:
      return {
        ...state,
        showValidationErrors: action.showValidationErrors
      };
    case frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR:
      return {
        ...state,
        prebookingError: action.prebookingError
      };
    case frankfurtCardActionTypes.SET_FRANKFURT_CARD_BOOKING_FORM:
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    case frankfurtCardActionTypes.TOGGLE_FRANKFURT_CARD_BOOKING_ERROR:
      return {
        ...state,
        bookingError: action.bookingError
      };
    case frankfurtCardActionTypes.SET_FRANKFURT_CARD_BOOKING_RESPONSE_DATA:
      return {
        ...state,
        bookingResponseData: action.bookingResponseData
      };
    case frankfurtCardActionTypes.TOGGLE_FRANKFURT_CARD_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: action.showPaymentModal
      };
    default:
      return state;
  }
};

export default frankfurtCardModule;

export const frankfurtCardActionCreators = {
  resetFrankfurtCardStateAction: () => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.RESET_FRANKFURT_CARD_STATE
    });
  },
  setSelectedOfferAction: (selectedOffer: FrankfurtCardOffer | null) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_SELECTED_OFFER,
      selectedOffer
    });
  },
  updatePassengersAction: (passengerList: FrankfurtCardPassenger[]) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PASSENGERS_LIST,
      passengerList
    });
  },
  updateShowValidationErrorsAction: (showValidationErrors: boolean) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_SHOW_VALIDATION_ERRORS,
      showValidationErrors
    });
  },
  updateBookingFormAction: (dataForBooking: FrankfurtCardBookingForm) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_BOOKING_FORM,
      dataForBooking
    });
  },
  clearPrebookingErrorAction: () => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR,
      prebookingError: null
    });
  },
  resetFrankfurtCardPrebookStateAction: () => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO,
      prebookingInfoResponse: null
    });
  },
  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.TOGGLE_FRANKFURT_CARD_BOOKING_ERROR,
      bookingError
    });
  },
  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({
      type: frankfurtCardActionTypes.TOGGLE_FRANKFURT_CARD_PAYMENT_MODAL,
      showPaymentModal
    });
  },
  getFrankfurtCardOffersAction:
    (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const paths = resolveFrankfurtCardApiPaths(transportContext);

      if (paths.kind !== "ok") {
        return;
      }

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      try {
        let data: FrankfurtCardOffersResponse;

        if (USE_MOCK_FRANKFURT_CARD) {
          // keep a small delay so loading UX matches real API
          await new Promise(resolve => setTimeout(resolve, 300));
          data = MOCK_FRANKFURT_CARD_OFFERS_RESPONSE;
        } else {
          const res = await ApiHelper.get(`${apiUrl}/${paths.mainPath}`);
          data = (res?.data ?? null) as FrankfurtCardOffersResponse;
        }

        dispatch({
          type: frankfurtCardActionTypes.LOAD_FRANKFURT_CARD_OFFERS,
          offers: data
        });

        // Preselect first offer only when nothing is selected yet
        const currentSelectedOffer = getState().frankfurtCard.selectedOffer as FrankfurtCardOffer | null;
        if (!currentSelectedOffer && data?.items?.[0]) {
          dispatch(frankfurtCardActionCreators.setSelectedOfferAction(data.items[0]));
        }
      } catch (_err: any) {
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  getPriceFrankfurtCardPrebookAction:
    (params: any, transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState()) || "";
      const paths = resolveFrankfurtCardApiPaths(transportContext);
      const passengerList = getState().frankfurtCard.passengerList as FrankfurtCardPassenger[];
      const selectedOffer = getState().frankfurtCard.selectedOffer as FrankfurtCardOffer | null;
      const requestOfferCode = params?.offer_code ?? selectedOffer?.offer_code ?? null;

      const updatePassengers = passengerList.map(passenger => ({
        first_name: passenger?.firstName?.trim(),
        last_name: passenger?.lastName?.trim()
      }));

      const rawStartDate = typeof params?.start_date === "string" ? params.start_date : "";
      const [datePart, timePart] = rawStartDate.split(" ");

      const basePayload = buildDestinationTransportPayload({
        date: datePart || "",
        time: timePart || "",
        departureAddress: params?.departureAddress ?? params?.origin ?? null,
        departureAddressId: params?.departureAddressId ?? null,
        destinationAddress: params?.destinationAddress ?? params?.destination ?? null,
        destinationAddressId: params?.destinationAddressId ?? null,
        milesAndMore: params?.milesAndMore ?? readAirlineMilesAndMoreNumber(),
        offerCode: requestOfferCode
      });

      const apiParams: Record<string, any> = {
        ...basePayload,
        details: {
          passengers: updatePassengers
        }
      };

      const isStaleEstimationResponse = () => {
        const currentOfferCode = getState().frankfurtCard.selectedOffer?.offer_code ?? null;
        return Boolean(requestOfferCode) && currentOfferCode !== requestOfferCode;
      };

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({
        type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR,
        prebookingError: null
      });

      if (paths.kind !== "ok") {
        dispatch({
          type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: null
        });
        dispatch({
          type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR,
          prebookingError:
            paths.kind === "invalid_airline"
              ? { code: "MISSING_IATA", message: "Missing airport code" }
              : { code: "UNSUPPORTED_PLATFORM", message: "Frankfurt Card is only available on Transfer Hub" }
        });
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
        return;
      }

      try {
        let data: any;

        if (USE_MOCK_FRANKFURT_CARD) {
          await new Promise(resolve => setTimeout(resolve, 300));
          if (USE_MOCK_FRANKFURT_CARD_ESTIMATION_ERROR) {
            // Mimic an axios error so the real catch path handles it
            throw Object.assign(new Error("Validation failed"), {
              response: { status: 400, data: MOCK_FRANKFURT_CARD_ESTIMATION_ERROR_RESPONSE }
            });
          }
          data = buildMockEstimationResponse(requestOfferCode, updatePassengers);
        } else {
          data = await postDestinationTransportEstimation({
            apiUrl,
            transportContext,
            payload: apiParams,
            v2: { path: "v2/transport/frankfurt-card" },
            airline: {
              path: iataCode => `v1/airports/${encodeURIComponent(iataCode)}/transports/frankfurt-card`
            }
          });
        }

        // Ignore late responses after the guest changed the selected offer mid-request.
        if (isStaleEstimationResponse()) {
          return;
        }

        dispatch({
          type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: data
        });
        return data;
      } catch (err: any) {
        if (isStaleEstimationResponse()) {
          return;
        }

        dispatch({
          type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: null
        });
        dispatch({
          type: frankfurtCardActionTypes.UPDATE_FRANKFURT_CARD_PREBOOK_API_ERROR,
          prebookingError: err?.response?.data
        });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  buyFrankfurtCardTicketAction:
    (params: any, navigate: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const state = getState().frankfurtCard;
      const dataForBooking = state.dataForBooking as FrankfurtCardBookingForm;
      const passengerList = state.passengerList as FrankfurtCardPassenger[];
      const selectedOffer = state.selectedOffer as FrankfurtCardOffer | null;
      const prebookingInfoResponse = state.prebookingInfoResponse;
      const paths = resolveFrankfurtCardApiPaths(transportContext);

      const errorField = {
        nameForm: checkForEmptyField(dataForBooking.nameForm),
        emailForm: isEmailInvalid(dataForBooking.emailForm) || checkForEmptyField(dataForBooking.emailForm),
        commentForm: false
      };

      dispatch(frankfurtCardActionCreators.toggleBookingErrorAction(false));

      if (Object.values(errorField).some(Boolean)) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField });
        return;
      }

      if (!selectedOffer || !passengerList.length || paths.kind !== "ok") {
        dispatch(frankfurtCardActionCreators.toggleBookingErrorAction(true));
        return;
      }

      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField });
      localStorage.removeItem("bookingResponseDataStorage");

      const apiUrl = selectApiBaseUrl(getState());
      const language = localStorage.getItem("i18nextLng") || "en";

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      const fullName = extractFullNameParts(dataForBooking.nameForm);

      // Units, date and travellers are rebuilt server-side from the estimation session.
      const apiParams = {
        summary: {
          start_date: params?.selectedDate,
          adult: passengerList.length,
          timeslot: "00:00",
          offer_code: params?.offer_code ?? selectedOffer.offer_code
        },
        personal_information: {
          first_name: fullName.firstName,
          last_name: fullName.lastName,
          email: dataForBooking.emailForm,
          phone: "",
          comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
        },
        ...buildAirlineMilesAndMoreBookingFields(transportContext),
        session_id: prebookingInfoResponse?.details?.session_id || prebookingInfoResponse?.session_id
      };

      try {
        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

        // We can get API status 200, with booking_status=error --> in that case we should show the error page instead of Payment Modal / Confirmation Page
        if (response.data?.booking_status === "error") {
          dispatch(frankfurtCardActionCreators.toggleBookingErrorAction(true));
          return;
        }

        dispatch(frankfurtCardActionCreators.toggleBookingErrorAction(false));
        dispatch({
          type: frankfurtCardActionTypes.SET_FRANKFURT_CARD_BOOKING_RESPONSE_DATA,
          bookingResponseData: response.data
        });

        // If price is 0 we will get payment_status=paid from API --> and we should redirect user directly to the /confirmation page (without payment modal)
        if (response.data?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(`/${language}/transport/frankfurt-card/confirmation`, { replace: true });
        } else {
          dispatch(frankfurtCardActionCreators.togglePaymentModalAction(true));
        }
      } catch (error: any) {
        handleErrorAlert(error);
        dispatch(frankfurtCardActionCreators.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    }
};
